import { useState } from "react"
import { Autocomplete, Box, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { DataInputDialog } from "lib/@dialogs/data-input-dialog"
import { BoundTextField } from "lib/@components/binding/bound-components"
import { If } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { invalidate } from "lib/graphql/cache"
import { addFentraGroupMember, removeFentraGroupMember } from "routes/qa/fentra/controllers/fentra-mutations"
import { FentraMemberChip } from "routes/qa/fentra/groups/fentra-member-chip"

export function FentraGroupDialog({ mode = "create", group, users = [] }) {
    const isEdit = mode === "edit"
    const initialValue = isEdit ? { displayName: group?.displayName ?? "", description: group?.description ?? "" } : {}

    // Member list is managed locally and synced to the server one call at a time.
    // Each add/remove is a SCIM call so there is no batch save — the dialog's OK button
    // only saves displayName / description.
    const [members, setMembers] = useState(() => group?.memberExternalIds ?? [])
    const [addInput, setAddInput] = useState(null)

    const memberSet = new Set(members)
    const addableUsers = users.filter(isAddable)

    return (
        <DataInputDialog initialValue={initialValue} noCancel={isEdit} okCaption={isEdit ? "OK" : "Create"}>
            <DialogTitle>{isEdit ? "Edit Group" : "Add Group"}</DialogTitle>
            <DialogContent>
                <BoundTextField
                    field="displayName"
                    label="Display Name"
                    fullWidth
                    sx={{ mb: 2, mt: 1 }}
                    yup="string.trim.required"
                />
                <BoundTextField field="description" label="Description" fullWidth sx={{ mb: 2 }} multiline rows={2} />

                <If value={isEdit}>
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Members
                        </Typography>
                        <If value={members.length > 0}>
                            {/* Bound passes users and onRemove into FentraMemberChip via context */}
                            <Bound users={users} onRemove={handleRemoveMember}>
                                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1 }}>
                                    <Repeat
                                        list={members}
                                        item={<FentraMemberChip />}
                                        keyFn={identityFn}
                                        pass="externalId"
                                    />
                                </Stack>
                            </Bound>
                        </If>
                        {/* Each selection fires an immediate SCIM addMember call */}
                        <Autocomplete
                            options={addableUsers}
                            getOptionLabel={getUserLabel}
                            value={addInput}
                            onChange={handleAddMember}
                            renderInput={renderAddInput}
                            size="small"
                            blurOnSelect
                        />
                    </Box>
                </If>
            </DialogContent>
        </DataInputDialog>
    )

    function isAddable(u) {
        return u.externalId && !memberSet.has(u.externalId)
    }

    function getUserLabel(u) {
        return `${u.displayName || u.userName} (${u.email || u.externalId})`
    }

    function renderAddInput(params) {
        return <TextField {...params} label="Add member" />
    }

    async function handleAddMember(_, user) {
        if (!user?.externalId || !group?._id) return
        setAddInput(null)
        try {
            const result = await addFentraGroupMember({ groupId: group._id, userExternalId: user.externalId })
            if (!result?.ok) {
                errorSnackbar(result?.error ?? "Failed to add member")
                return
            }
            setMembers((prev) => [...prev, user.externalId])
            invalidate("fentraGroups")
        } catch (err) {
            errorSnackbar(err?.message || "Failed to add member")
        }
    }

    async function handleRemoveMember(externalId) {
        if (!group?._id) return
        try {
            const result = await removeFentraGroupMember({ groupId: group._id, userExternalId: externalId })
            if (!result?.ok) {
                errorSnackbar(result?.error ?? "Failed to remove member")
                return
            }
            setMembers((prev) => prev.filter(notExternalId))
            invalidate("fentraGroups")
        } catch (err) {
            errorSnackbar(err?.message || "Failed to remove member")
        }

        function notExternalId(id) {
            return id !== externalId
        }
    }
}

function identityFn(x) {
    return x
}
